/* MinerOps projection engine — pure functions, no React.
   Single source of truth for all financial math. The dashboard hook and the
   plan-comparison view both call these with an explicit params object. */
import {
  GROWTH_RATES, SP500_START_PRICE, AVG_DAYS_PER_MONTH, TWO_POW_32,
  MAX_PROJECTION_MONTHS, IO_LOAN_SENTINEL, MACRS_RATES, BONUS_DEP_RATES, TAX_YEAR,
} from './constants';

// params: { timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed,
//            poolFee, uptimeFactor, blockReward, difficulty, difficultyGrowth,
//            startingTreasury, startingCash, halvingMonths }
export function calculateStrategy(strategy, params) {
  const { timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed,
    poolFee, uptimeFactor, blockReward, difficulty, difficultyGrowth,
    startingTreasury, startingCash, halvingMonths } = params;
    if (strategy.isBenchmark) {
      return { monthlyData: [], annualData: [], finalTreasury: 0, finalNAV: 0, totalBTCMined: 0, totalBTCSold: 0, totalBTCKept: 0, totalRevenue: 0, totalProfit: 0 };
    }

    const months = timeHorizon * 12;
    const monthlyData = [], annualData = [];
    const growthRates = GROWTH_RATES;
    const monthlyPriceGrowth = Math.pow(1 + (growthRates[priceScenario] || 0.15), 1/12) - 1;

    const calcPayment = (p, r, t, io) => {
      if (p <= 0 || t <= 0) return 0;
      const mr = r / 12;
      if (mr === 0) return io ? 0 : p / t;
      return io ? p * mr : (p * mr * Math.pow(1 + mr, t)) / (Math.pow(1 + mr, t) - 1);
    };

    const initPayment = strategy.useInitialFinancing ? calcPayment(strategy.initialLoanAmount, strategy.initialLoanRate, strategy.initialLoanTerm, strategy.initialInterestOnly) : 0;
    const loans = [];
    if (strategy.useInitialFinancing) {
      loans.push({ start: 1, payment: initPayment, endMonth: strategy.initialInterestOnly ? IO_LOAN_SENTINEL : strategy.initialLoanTerm, io: strategy.initialInterestOnly, amount: strategy.initialLoanAmount, rate: strategy.initialLoanRate, balance: strategy.initialLoanAmount });
    }
    strategy.expansions.forEach(e => {
      if (e.useLoan && e.miners > 0 && e.loanAmount > 0) {
        loans.push({ start: e.month, payment: calcPayment(e.loanAmount, e.loanRate, e.loanTerm, e.interestOnly), endMonth: e.interestOnly ? IO_LOAN_SENTINEL : e.month + e.loanTerm - 1, io: e.interestOnly, amount: e.loanAmount, rate: e.loanRate, balance: 0 });
      }
    });

    const getReward = (m) => { let r = blockReward; halvingMonths.forEach(h => { if (m >= h) r /= 2; }); return r; };

    const cohorts = [];
    if (strategy.startingMiners > 0) {
      cohorts.push({ id: 'initial', startMonth: 1, count: strategy.startingMiners, hashRate: strategy.hashRate, powerDraw: strategy.powerDraw, minerPrice: strategy.minerPrice, lifespan: strategy.initialMinerLifespan, resaleValue: strategy.initialResaleValue, opexCoverage: strategy.initialOpexCoverage || 'btc', isOnline: true });
    }
    strategy.expansions.forEach((e, i) => {
      if (e.miners > 0) {
        cohorts.push({ id: `exp${i}`, startMonth: e.month, count: e.miners, hashRate: e.hashRate, powerDraw: e.powerDraw, minerPrice: e.minerPrice, lifespan: e.lifespan, resaleValue: e.resaleValue, opexCoverage: e.opexCoverage || 'btc', isOnline: true });
      }
    });

    let cumBTC = 0, treasury = startingTreasury, cash = startingCash || 0;
    let totalCapex = strategy.startingMiners * strategy.minerPrice;
    let totalResaleProceeds = 0;
    let totalOtherIncomeUsed = 0;
    let totalLoanProceeds = 0;
    let totalCapexPaid = 0;
    let totalOutOfPocket = startingTreasury * btcPrice + (startingCash || 0);

    // Capital placed in service in month 1. Tracked separately from monthCapex
    // because the cash for it is deducted here, before the month loop — adding
    // it to monthCapex would double-count against cash and out-of-pocket.
    //
    // Sunk fleets are excluded: the client bought that hardware in an earlier
    // year and any deduction belongs to that year's return.
    const initialCapexPlacedInService = strategy.initialCapexIsSunk
      ? 0
      : strategy.startingMiners * strategy.minerPrice;

    if (!strategy.initialCapexIsSunk) {
      const initialCapexCash = strategy.startingMiners * strategy.minerPrice;
      cash -= initialCapexCash;
      totalCapexPaid += initialCapexCash;
      totalOutOfPocket += initialCapexCash;
    }

    if (strategy.useInitialFinancing && strategy.initialLoanAmount > 0) {
      cash += strategy.initialLoanAmount;
      totalLoanProceeds += strategy.initialLoanAmount;
    }

    const cohortStatus = cohorts.map(c => ({ ...c }));

    // Last month any miner earns; after this the fleet is fully retired.
    const fleetOfflineMonth = cohorts.length
      ? Math.max(...cohorts.map(c => c.startMonth + c.lifespan - 1))
      : 0;
    const monthlyDiffGrowth = Math.pow(1 + difficultyGrowth, 1/12) - 1;

    for (let m = 1; m <= Math.min(months, MAX_PROJECTION_MONTHS); m++) {
      const currentDifficulty = difficulty * Math.pow(1 + monthlyDiffGrowth, m - 1);
      const currentNetworkHashrate = (currentDifficulty * 1e12 * Math.pow(2, 32)) / (600 * 1e18);
      const reward = getReward(m);
      const price = btcPrice * Math.pow(1 + monthlyPriceGrowth, m - 1);

      let monthResale = 0;
      let monthCapex = 0;
      let monthLoanProceeds = 0;

      cohortStatus.forEach(cohort => {
        if (!cohort.isOnline) return;
        if (m < cohort.startMonth) return;
        const monthsActive = m - cohort.startMonth + 1;
        if (monthsActive > cohort.lifespan) {
          cohort.isOnline = false;
          monthResale += cohort.count * cohort.resaleValue;
        }
      });

      totalResaleProceeds += monthResale;

      strategy.expansions.forEach(e => {
        if (m === e.month && e.miners > 0) {
          const capex = e.miners * e.minerPrice;
          totalCapex += capex;
          cash -= capex;
          monthCapex += capex;
          totalCapexPaid += capex;
          if (e.useLoan && e.loanAmount > 0) {
            cash += e.loanAmount;
            monthLoanProceeds += e.loanAmount;
            totalLoanProceeds += e.loanAmount;
          }
        }
      });

      let totalHashrateTH = 0, totalPowerW = 0, totalMiners = 0;
      let opexCoveredByBtc = 0;
      let opexCoveredByOtherIncome = 0;
      let totalElectricityCost = 0;
      let totalHostingCost = 0;

      cohortStatus.forEach(cohort => {
        if (cohort.isOnline && m >= cohort.startMonth) {
          totalHashrateTH += cohort.count * cohort.hashRate;
          totalPowerW += cohort.count * cohort.powerDraw;
          totalMiners += cohort.count;

          const cohortKwh = cohort.count * cohort.powerDraw * 24 * AVG_DAYS_PER_MONTH / 1000;
          const cohortPowerCost = cohortKwh * electricityCost;
          const cohortHostingCost = cohort.count * hostingFixed;
          const cohortOpex = cohortPowerCost + cohortHostingCost;
          totalElectricityCost += cohortPowerCost;
          totalHostingCost += cohortHostingCost;

          if (cohort.opexCoverage === 'otherIncome') {
            opexCoveredByOtherIncome += cohortOpex;
          } else {
            opexCoveredByBtc += cohortOpex;
          }
        }
      });

      const btcPerDay = currentDifficulty > 0
        ? (totalHashrateTH * reward * 86400) / (currentDifficulty * TWO_POW_32)
        : 0;
      const btcMinedGross = btcPerDay * AVG_DAYS_PER_MONTH * uptimeFactor;
      const btcPoolFee = btcMinedGross * poolFee;
      const btcMined = btcMinedGross - btcPoolFee;

      cumBTC += btcMined;
      const btcMinedValue = btcMined * price;
      const btcMinedGrossValue = btcMinedGross * price;

      let loanPay = 0;
      let totalOutstandingDebt = 0;
      loans.forEach(l => {
        // Activate loan balance when loan starts
        if (m === l.start && l.balance === 0) l.balance = l.amount;
        if (m >= l.start && l.balance > 0) {
          if (m <= l.endMonth) {
            loanPay += l.payment;
            if (!l.io) {
              // Amortizing: interest portion = balance * monthly rate, rest is principal
              const monthlyInterest = l.balance * (l.rate / 12);
              const principalPaid = Math.min(l.payment - monthlyInterest, l.balance);
              l.balance = Math.max(0, l.balance - principalPaid);
            }
            // IO loans: balance stays constant (interest-only payments)
          }
          totalOutstandingDebt += l.balance;
        }
      });

      const totalPowerHostingOpex = opexCoveredByBtc + opexCoveredByOtherIncome;

      let btcSoldForOpex = 0;
      let btcSoldExtra = 0;
      let btcSoldForCashBuffer = 0;

      const opexNeedingOtherIncome = opexCoveredByOtherIncome + loanPay;

      // ── Outside funding source ──
      // 'fixed': contribute the full monthly amount while the window is open
      //          (legacy behavior, now windowed).
      // 'shortfall': draw only what the externally-routed bills need, up to
      //          the monthly cap — no surplus accumulates as cash.
      // Every dollar drawn is contributed capital: it counts in
      // totalOutOfPocket (ROI denominator) and is mirrored by the DCA paths.
      const fundingCap = strategy.otherIncome || 0;
      const fundingActive = m >= (strategy.fundingStartMonth || 1)
        && m <= (strategy.fundingEndMonth || IO_LOAN_SENTINEL);
      const externalFundsDrawn = !fundingActive ? 0
        : (strategy.fundingMode === 'shortfall'
          ? Math.min(fundingCap, opexNeedingOtherIncome)
          : fundingCap);
      const opexNeedingBtcSales = opexCoveredByBtc;

      if (opexNeedingBtcSales > 0 && price > 0) {
        btcSoldForOpex = Math.min(btcMined, opexNeedingBtcSales / price);
      }

      const btcAfterOpex = btcMined - btcSoldForOpex;
      btcSoldExtra = btcAfterOpex * (strategy.sellPercent || 0);

      const preliminaryCashFlow = (btcSoldForOpex + btcSoldExtra) * price - opexCoveredByBtc + externalFundsDrawn - opexNeedingOtherIncome + monthResale;
      const preliminaryCash = cash + preliminaryCashFlow;

      const minCashBuffer = strategy.cashBufferMin || 5000;
      const useAutoSell = strategy.autoSellForCashBuffer === true;

      if (useAutoSell && preliminaryCash < minCashBuffer && price > 0) {
        const cashNeeded = minCashBuffer - preliminaryCash;
        const btcAvailableToSell = btcMined - btcSoldForOpex - btcSoldExtra;
        btcSoldForCashBuffer = Math.min(btcAvailableToSell, cashNeeded / price);
        if (btcSoldForCashBuffer < cashNeeded / price && treasury > 0) {
          const additionalNeeded = (cashNeeded / price) - btcSoldForCashBuffer;
          const fromTreasury = Math.min(treasury, additionalNeeded);
          btcSoldForCashBuffer += fromTreasury;
          treasury -= fromTreasury;
        }
      }

      const btcSoldTotal = btcSoldForOpex + btcSoldExtra + btcSoldForCashBuffer;
      const btcKept = btcMined - (btcSoldForOpex + btcSoldExtra + Math.min(btcSoldForCashBuffer, btcMined - btcSoldForOpex - btcSoldExtra));
      treasury += btcKept;

      const revenue = btcSoldTotal * price;
      const poolFeeCost = btcPoolFee * price;

      const operatingCashFlow = revenue - opexCoveredByBtc + externalFundsDrawn - opexNeedingOtherIncome + monthResale;
      cash += operatingCashFlow;

      totalOtherIncomeUsed += externalFundsDrawn;

      const totalOpexDisplay = totalPowerHostingOpex + poolFeeCost;
      const dailyRevenue = btcPerDay * (1 - poolFee) * price;
      const dailyPowerCost = (totalPowerW * 24 / 1000) * electricityCost;
      const dailyHostingCost = totalMiners * hostingFixed / AVG_DAYS_PER_MONTH;
      const dailyCost = dailyPowerCost + dailyHostingCost;
      const dailyProfit = dailyRevenue - dailyCost;

      const netCapexFromCash = Math.max(0, monthCapex - monthLoanProceeds);
      totalOutOfPocket += netCapexFromCash + externalFundsDrawn;

      const nav = treasury * price + cash - totalOutstandingDebt;
      const roi = totalOutOfPocket > 0 ? ((nav - totalOutOfPocket) / totalOutOfPocket) * 100 : 0;

      monthlyData.push({
        month: m, year: Math.ceil(m/12), miners: totalMiners,
        totalHashrateTH, totalPowerW, hashratePH: totalHashrateTH / 1000,
        networkHashrateEH: currentNetworkHashrate, difficultyT: currentDifficulty,
        blockReward: reward, btcMined, btcMinedGross, btcPoolFee, btcMinedValue, btcMinedGrossValue, btcPerDay,
        cumBTC, btcPrice: price, revenue, totalOpEx: totalOpexDisplay,
        loanPayment: loanPay, netProfit: operatingCashFlow, btcSoldTotal, btcKept,
        treasuryBTC: treasury, resaleProceeds: monthResale, totalResaleProceeds,
        nav, roi, totalCapex, totalOutOfPocket, outstandingDebt: totalOutstandingDebt, cash, dailyRevenue, dailyCost, dailyProfit, poolFeeCost,
        monthCapex, monthLoanProceeds, capexThisMonth: monthCapex,
        // Basis eligible for depreciation this month. Differs from monthCapex
        // in month 1, which is exactly the case the old code missed: a plan
        // whose capital is all in the starting fleet depreciated nothing.
        capexPlacedInService: monthCapex + (m === 1 ? initialCapexPlacedInService : 0),
        externalFundsDrawn, otherIncomeAvailable: externalFundsDrawn, opexNeedingOtherIncome, opexCoveredByBtc, opexCoveredByOtherIncome,
        electricityCost: totalElectricityCost, hostingCost: totalHostingCost
      });
    }

    // Single-pass annual aggregation (avoids O(years * months) filter+reduce)
    {
      const yearBuckets = {};
      for (const d of monthlyData) {
        const y = d.year;
        if (!yearBuckets[y]) yearBuckets[y] = { months: [], btcMined: 0, btcMinedValue: 0, btcSold: 0, btcKept: 0, revenue: 0, opEx: 0, loanPayments: 0, poolFees: 0, electricityCost: 0, hostingCost: 0, netProfit: 0, resaleProceeds: 0, capexThisYear: 0, loanProceedsThisYear: 0, last: d };
        const b = yearBuckets[y];
        b.btcMined += d.btcMined; b.btcMinedValue += d.btcMinedValue; b.btcSold += d.btcSoldTotal; b.btcKept += d.btcKept;
        b.revenue += d.revenue; b.opEx += d.totalOpEx; b.loanPayments += (d.loanPayment || 0); b.poolFees += d.poolFeeCost; b.electricityCost += (d.electricityCost || 0); b.hostingCost += (d.hostingCost || 0);
        b.netProfit += d.netProfit; b.resaleProceeds += d.resaleProceeds; b.capexThisYear += d.capexPlacedInService; b.loanProceedsThisYear += d.monthLoanProceeds;
        b.last = d;
      }
      for (let y = 1; y <= timeHorizon; y++) {
        const b = yearBuckets[y];
        if (!b) continue;
        const last = b.last;
        annualData.push({
          year: y, btcMined: b.btcMined, btcMinedValue: b.btcMinedValue, btcSold: b.btcSold, btcKept: b.btcKept,
          revenue: b.revenue, opEx: b.opEx, loanPayments: b.loanPayments, poolFees: b.poolFees, electricityCost: b.electricityCost, hostingCost: b.hostingCost, netProfit: b.netProfit,
          treasuryBTC: last.treasuryBTC, btcPrice: last.btcPrice, nav: last.nav, blockReward: last.blockReward,
          resaleProceeds: b.resaleProceeds, roi: last.roi, minersEOY: last.miners, cash: last.cash,
          networkHashrateEH: last.networkHashrateEH, difficultyT: last.difficultyT,
          capexThisYear: b.capexThisYear, loanProceedsThisYear: b.loanProceedsThisYear,
        });
      }
    }

    const cur = monthlyData[0] || {};
    const fin = monthlyData[monthlyData.length - 1] || {};

    const breakEvenMonth = monthlyData.findIndex(d => d.totalOutOfPocket > 0 && d.nav > d.totalOutOfPocket) + 1 || null;

    const totalBTCMinedGrossValue = monthlyData.reduce((s,d) => s + d.btcMinedGrossValue, 0);
    const totalOpExAll = monthlyData.reduce((s,d) => s + d.totalOpEx, 0);
    // Mining multiplier: use totalCapexPaid (excludes sunk) for the cost basis
    const effectiveCapexForMultiplier = strategy.initialCapexIsSunk ? totalCapexPaid : totalCapex;
    const totalAllCosts = effectiveCapexForMultiplier + totalOpExAll;
    const miningMultiplier = totalAllCosts > 0 ? totalBTCMinedGrossValue / totalAllCosts : 0;

    // Payback: when cumulative mining revenue + resale covers cumulative out-of-pocket costs
    let cumMiningInflow = 0;
    let paybackMonth = null;
    for (const d of monthlyData) {
      cumMiningInflow += d.revenue + d.resaleProceeds;
      if (d.totalOutOfPocket > 0 && cumMiningInflow >= d.totalOutOfPocket && paybackMonth === null) { paybackMonth = d.month; }
    }

    return {
      monthlyData, annualData, current: cur,
      totalBTCMined: monthlyData.reduce((s,d) => s + d.btcMined, 0),
      totalBTCSold: monthlyData.reduce((s,d) => s + d.btcSoldTotal, 0),
      totalBTCKept: monthlyData.reduce((s,d) => s + d.btcKept, 0),
      totalRevenue: monthlyData.reduce((s,d) => s + d.revenue, 0),
      totalProfit: monthlyData.reduce((s,d) => s + d.netProfit, 0),
      finalTreasury: fin.treasuryBTC || startingTreasury,
      finalNAV: fin.nav || 0, finalCash: fin.cash || 0, finalBTCPrice: fin.btcPrice || btcPrice,
      initialCapEx: strategy.startingMiners * strategy.minerPrice,
      totalCapEx: fin.totalCapex || strategy.startingMiners * strategy.minerPrice,
      totalCapExPaid: strategy.initialCapexIsSunk ? totalCapexPaid : (fin.totalCapex || strategy.startingMiners * strategy.minerPrice),
      initialCapexIsSunk: !!strategy.initialCapexIsSunk,
      totalResaleProceeds: fin.totalResaleProceeds || 0,
      halvingsInPeriod: halvingMonths.filter(h => h <= months).length,
      finalROI: fin.roi || 0, totalOtherIncomeUsed, totalLoanProceeds, totalCapexPaid,
      breakEvenMonth, miningMultiplier, totalBTCMinedGrossValue, paybackMonth, fleetOfflineMonth
    };
}

// DCA benchmarks mirror the mining strategy's actual monthly cash outflows.
export function calculateDCAStrategies(miningResults, strategy, params) {
  const { priceScenario, btcPrice, startingTreasury, dcaSpReturn, timeHorizon,
    dcaBtcEnabled = true, dcaSpEnabled = true } = params;
    const md = miningResults.monthlyData;
    if (!md || md.length === 0) return { btcDCA: null, spDCA: null };

    const growthRates = GROWTH_RATES;
    const scenarioNames = { 1: 'Bear 5%', 2: 'Base 15%', 3: 'Bull 30%', 4: 'Historical 75%' };

    // Extract cash outflows from mining (same logic as equivalentDCA)
    const initialCapexCash = miningResults.initialCapexIsSunk ? 0 : (strategy.startingMiners * strategy.minerPrice);
    const monthlyCashOuts = [];
    for (let i = 0; i < md.length; i++) {
      const d = md[i];
      const pocketDraw = d.externalFundsDrawn ?? ((d.opexCoveredByOtherIncome || 0) + (d.loanPayment || 0));
      const grossCapex = d.monthCapex || d.capexThisMonth || 0;
      const loanProceeds = d.monthLoanProceeds || 0;
      const netCapexFromCash = Math.max(0, grossCapex - loanProceeds);
      let cashOut = pocketDraw + netCapexFromCash;
      if (i === 0) cashOut += initialCapexCash;
      monthlyCashOuts.push(cashOut);
    }

    // ── BTC DCA: buy BTC each month with the same cash mining consumed ──
    // Use the SAME price scenario as mining for a true apples-to-apples comparison
    const monthlyBTCGrowth = Math.pow(1 + (growthRates[priceScenario] || 0.15), 1/12) - 1;
    let btcHoldings = startingTreasury;
    let totalBTCInvested = startingTreasury * btcPrice;
    const btcMonthlyData = [];

    for (let m = 1; m <= md.length; m++) {
      const price = btcPrice * Math.pow(1 + monthlyBTCGrowth, m - 1);
      const cashOut = monthlyCashOuts[m - 1];
      const btcBought = price > 0 ? cashOut / price : 0;
      btcHoldings += btcBought;
      totalBTCInvested += cashOut;
      const nav = btcHoldings * price;
      const roi = totalBTCInvested > 0 ? ((nav - totalBTCInvested) / totalBTCInvested) * 100 : 0;
      btcMonthlyData.push({ month: m, year: Math.ceil(m / 12), btcBought, totalBTCInvested, treasuryBTC: btcHoldings, btcPrice: price, nav, roi, cash: 0 });
    }

    // ── S&P DCA: invest the same cash in S&P 500 each month ──
    const monthlySPGrowth = Math.pow(1 + dcaSpReturn / 100, 1/12) - 1;
    const spStartPrice = SP500_START_PRICE;
    let spShares = (startingTreasury * btcPrice) / spStartPrice;
    let totalSPInvested = startingTreasury * btcPrice;
    const spMonthlyData = [];
    const monthlyBTCPriceGrowth = Math.pow(1 + (growthRates[priceScenario] || 0.15), 1/12) - 1;

    for (let m = 1; m <= md.length; m++) {
      const spPrice = spStartPrice * Math.pow(1 + monthlySPGrowth, m - 1);
      const btcPriceAtMonth = btcPrice * Math.pow(1 + monthlyBTCPriceGrowth, m - 1);
      const cashOut = monthlyCashOuts[m - 1];
      const sharesBought = spPrice > 0 ? cashOut / spPrice : 0;
      spShares += sharesBought;
      totalSPInvested += cashOut;
      const spValue = spShares * spPrice;
      const roi = totalSPInvested > 0 ? ((spValue - totalSPInvested) / totalSPInvested) * 100 : 0;
      const btcEquivalent = btcPriceAtMonth > 0 ? spValue / btcPriceAtMonth : 0;
      spMonthlyData.push({ month: m, year: Math.ceil(m / 12), sharesBought, totalSPInvested, spShares, spPrice, spValue, treasuryBTC: btcEquivalent, btcPrice: btcPriceAtMonth, nav: spValue, roi, cash: 0 });
    }

    const buildAnnualData = (monthlyData) => {
      const annualData = [];
      for (let y = 1; y <= timeHorizon; y++) {
        const yd = monthlyData.filter(d => d.year === y);
        if (yd.length > 0) {
          const last = yd[yd.length - 1];
          annualData.push({ year: y, treasuryBTC: last.treasuryBTC, btcPrice: last.btcPrice, nav: last.nav, roi: last.roi });
        }
      }
      return annualData;
    };

    const btcFinal = btcMonthlyData[btcMonthlyData.length - 1] || {};
    const spFinal = spMonthlyData[spMonthlyData.length - 1] || {};

    return {
      btcDCA: dcaBtcEnabled ? {
        monthlyData: btcMonthlyData, annualData: buildAnnualData(btcMonthlyData),
        finalTreasury: btcFinal.treasuryBTC || 0, finalNAV: btcFinal.nav || 0, finalROI: btcFinal.roi || 0,
        finalCash: 0, totalCapEx: totalBTCInvested, totalBTCMined: 0, totalBTCSold: 0,
        totalInvested: totalBTCInvested, btcPurchased: btcFinal.treasuryBTC - startingTreasury,
        name: `DCA BTC (${scenarioNames[priceScenario]})`, color: '#F7931A'
      } : null,
      spDCA: dcaSpEnabled ? {
        monthlyData: spMonthlyData, annualData: buildAnnualData(spMonthlyData),
        finalTreasury: spFinal.treasuryBTC || 0, finalNAV: spFinal.nav || 0, finalROI: spFinal.roi || 0,
        finalCash: 0, totalCapEx: totalSPInvested, totalBTCMined: 0, totalBTCSold: 0,
        totalInvested: totalSPInvested, btcPurchased: 0,
        name: `DCA S&P (${dcaSpReturn}%/yr)`, color: '#4CAF50'
      } : null
    };
}

// params: { effectiveIncomeTaxRate, stateTaxRate, useBonusDepreciation }
export function calculateTaxBenefits(totalCapEx, annualData, params) {
  const { effectiveIncomeTaxRate, stateTaxRate, useBonusDepreciation } = params;
    if (totalCapEx <= 0) return { totalTaxSavings: 0, effectiveCapEx: totalCapEx, yearlyBreakdown: [] };

    const bonusRatesByYear = BONUS_DEP_RATES;
    const capexByYear = {};
    const startYear = TAX_YEAR;

    if (annualData) {
      annualData.forEach((year, i) => { capexByYear[startYear + i] = year.capexThisYear || 0; });
    } else {
      capexByYear[startYear] = totalCapEx;
    }

    const yearlyBreakdown = [];
    let totalTaxSavings = 0;

    for (let year = startYear; year <= startYear + 10; year++) {
      let yearDepreciation = 0;
      Object.entries(capexByYear).forEach(([purchaseYear, capex]) => {
        if (capex <= 0) return;
        const purchaseYearNum = parseInt(purchaseYear);
        const yearsAfterPurchase = year - purchaseYearNum;
        if (yearsAfterPurchase < 0 || yearsAfterPurchase >= MACRS_RATES.length) return;

        if (yearsAfterPurchase === 0 && useBonusDepreciation) {
          const bonusRate = bonusRatesByYear[purchaseYearNum] || 0;
          const bonusAmount = capex * bonusRate;
          const remainingBasis = capex - bonusAmount;
          yearDepreciation += bonusAmount + (remainingBasis * MACRS_RATES[0]);
        } else if (yearsAfterPurchase === 0) {
          yearDepreciation += capex * MACRS_RATES[0];
        } else {
          const bonusRate = useBonusDepreciation ? (bonusRatesByYear[purchaseYearNum] || 0) : 0;
          const remainingBasis = capex * (1 - bonusRate);
          yearDepreciation += remainingBasis * MACRS_RATES[yearsAfterPurchase];
        }
      });

      const taxSavings = yearDepreciation * ((effectiveIncomeTaxRate + stateTaxRate) / 100);
      totalTaxSavings += taxSavings;
      if (yearDepreciation > 0) {
        yearlyBreakdown.push({ year, depreciation: yearDepreciation, taxSavings, cumulative: totalTaxSavings });
      }
    }

    return { totalTaxSavings, effectiveCapEx: totalCapEx - totalTaxSavings, yearlyBreakdown, bonusDepreciationUsed: useBonusDepreciation };
}


/* ═══════ Plan economics: cost basis and the Mining Multiple ═══════

   The Mining Multiple is an AFTER-TAX figure. Write-offs are the whole point
   of mining rather than buying spot — before tax the multiple sits near 1.0x
   and the comparison looks pointless, which is not what it actually says.

   This lives here, rather than in the dashboard hook, because two callers need
   identical answers: the live dashboard (via useDashboardState) and Compare,
   which replays saved plan snapshots. They previously computed this separately
   and disagreed — Compare showed the pre-tax number while every other screen
   showed after-tax.

   results  — output of calculateStrategy()
   tax      — a plan snapshot's `tax` block (may be undefined on old plans)
   opts     — { btcPrice, timeHorizon, stateTaxRates, feieExclusion, nonConformingStates }
*/
export function computePlanEconomics(results, tax, opts) {
  if (!results || !results.totalBTCMined || results.totalBTCMined <= 0) return null;

  const { btcPrice, timeHorizon, stateTaxRates = [], feieExclusion = 0 } = opts || {};
  const t = tax || {};
  const taxEnabled = t.showAfterTaxEconomics !== false && t.taxRate != null;

  const totalOpEx = results.annualData?.reduce((s, y) => s + (y.opEx || 0), 0) || 0;
  const totalCapExPaid = results.totalCapExPaid || results.totalCapEx || 0;
  const totalBTCMined = results.totalBTCMined;

  // ── Effective federal rate, FEIE-blended (mirrors the dashboard exactly) ──
  let effectiveIncomeTaxRate = 0;
  let stateTaxRate = 0;
  if (taxEnabled) {
    const taxRate = t.taxRate || 0;
    stateTaxRate = (stateTaxRates.find(s => s.code === t.stateCode) || stateTaxRates[0] || { rate: 0 }).rate;

    const feieApplies = t.ownerResidency === 'us_citizen_abroad';
    const totalEarnedIncome = (t.otherSEIncome || 0) + (t.w2SSWages || 0);
    const feieRemaining = Math.max(0, feieExclusion - totalEarnedIncome);
    const feieCoversIncome = feieApplies && totalEarnedIncome <= feieExclusion;

    if (!feieApplies || !feieCoversIncome) {
      effectiveIncomeTaxRate = taxRate;
    } else {
      const years = Math.max(1, timeHorizon || 1);
      const avgMiningProfit = (results.annualData || [])
        .reduce((s, y) => s + Math.max(0, (y.btcMinedValue || 0) - (y.opEx || 0)), 0) / years;
      effectiveIncomeTaxRate = avgMiningProfit <= feieRemaining
        ? 0
        : taxRate * (1 - feieRemaining / avgMiningProfit);
    }
  }

  const taxBenefits = taxEnabled
    ? calculateTaxBenefits(results.totalCapEx || 0, results.annualData, {
        effectiveIncomeTaxRate, stateTaxRate, useBonusDepreciation: true,
      })
    : null;

  // Hardware depreciation is only half the deduction. Electricity, hosting and
  // pool fees are ordinary business expenses and are deductible too — the same
  // treatment the DCA comparison already applies. Counting depreciation alone
  // understated the write-offs by roughly two thirds on a long horizon, which
  // made mining look worse than it is against buying spot.
  const depSavings = taxBenefits?.totalTaxSavings || 0;
  const combinedRate = taxEnabled ? (effectiveIncomeTaxRate + stateTaxRate) / 100 : 0;
  const opExTaxSavings = totalOpEx * combinedRate;
  const taxSavings = depSavings + opExTaxSavings;

  const grossCostPerBTC = (totalCapExPaid + totalOpEx) / totalBTCMined;
  const netCostPerBTC = (totalCapExPaid + totalOpEx - taxSavings) / totalBTCMined;

  /* ── Breakeven and payback, after tax ──────────────────────────────────
     The engine computes both against totalOutOfPocket with no tax offset, so
     every month it reported was later than reality. Write-offs are a return of
     capital: they reduce what the client is actually out.

     TIMING: savings are credited at the END of the projection year in which the
     deduction is taken. Deductions reduce quarterly estimates through the year
     and settle at filing, so year-end is a fair midpoint — earlier than filing,
     later than the month the expense hits. Stated rather than hidden because it
     moves the answer, and a CPA will ask.

     Both pre-tax figures are kept. "Month 101, or month 74 once write-offs
     land" is a stronger and more honest line than either number alone. */
  const md = results.monthlyData || [];
  const annual = results.annualData || [];
  let postTaxBreakEvenMonth = null;
  let postTaxPaybackMonth = null;
  const cumulativeTaxSavingsByMonth = [];

  if (taxEnabled && md.length) {
    const depByYear = taxBenefits?.yearlyBreakdown || [];
    // Savings earned in projection year Y, credited at month Y*12.
    const savingsAtMonth = new Map();
    annual.forEach((y, i) => {
      const dep = depByYear[i]?.taxSavings || 0;
      const ops = (y.opEx || 0) * combinedRate;
      const total = dep + ops;
      if (total > 0) savingsAtMonth.set((i + 1) * 12, total);
    });

    let cumTax = 0;
    let cumInflow = 0;
    for (const d of md) {
      // Exposed so a chart can draw expenses net of write-offs without
      // recomputing the schedule. Value is the cumulative saving realised
      // BEFORE this month's credit, matching the threshold tests below.
      cumulativeTaxSavingsByMonth.push({ month: d.month, cumulativeTaxSavings: cumTax });
      cumInflow += (d.revenue || 0) + (d.resaleProceeds || 0);
      const oop = d.totalOutOfPocket || 0;
      if (oop > 0) {
        if (postTaxBreakEvenMonth === null && (d.nav || 0) > oop - cumTax) postTaxBreakEvenMonth = d.month;
        if (postTaxPaybackMonth === null && cumInflow + cumTax >= oop) postTaxPaybackMonth = d.month;
      }
      // Credit at the close of the month, so a year's savings cannot satisfy
      // a threshold before that year has actually finished.
      cumTax += savingsAtMonth.get(d.month) || 0;
    }
  }

  const totalBTCMinedGrossValue = results.totalBTCMinedGrossValue || 0;
  const avgSpotPrice = totalBTCMined > 0 && totalBTCMinedGrossValue > 0
    ? totalBTCMinedGrossValue / totalBTCMined
    : (btcPrice + (results.finalBTCPrice || btcPrice)) / 2;

  const grossMultiple = results.miningMultiplier || 0;
  const miningMultiple = taxSavings > 0 && netCostPerBTC > 0
    ? grossMultiple * (grossCostPerBTC / netCostPerBTC)
    : grossMultiple;

  return {
    grossCostPerBTC, netCostPerBTC, avgSpotPrice,
    savingsVsSpot: avgSpotPrice - netCostPerBTC,
    savingsPercent: avgSpotPrice > 0 ? ((avgSpotPrice - netCostPerBTC) / avgSpotPrice) * 100 : 0,
    totalBTCMined, totalOpEx, totalCapExPaid,
    taxSavings, depSavings, opExTaxSavings, miningMultiple, grossMultiple,
    // Post-tax timings; null when tax analysis is off or never reached.
    // Pre-tax equivalents stay on the results object as breakEvenMonth/paybackMonth.
    postTaxBreakEvenMonth, postTaxPaybackMonth,
    cumulativeTaxSavingsByMonth,
    // Surfaced so screens can show WHICH tax position produced the figure
    // rather than quietly applying one, and can label a pre-tax fallback.
    taxApplied: taxEnabled && taxSavings > 0,
    combinedRate: taxEnabled ? effectiveIncomeTaxRate + stateTaxRate : null,
  };
}
